import { ValidationAction, ValidationAssetType } from "@/api/validation-types";
import { RejectWholeEpisodeDialog } from "@/components/episodes/RejectWholeEpisodeDialog";
import { MaskDetails } from "@/components/images/MaskDetails";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { RejectionDialog } from "@/components/validation/RejectionDialog";
import { ValidationControls } from "@/components/validation/ValidationControls";
import { useRejectWholeEpisode } from "@/hooks/use-reject-whole-episode";
import { useMaskById } from "@/queries/masks/use-mask-by-id";
import { useValidateMask } from "@/queries/validation/use-validate-mask";
import { Box } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";

export function MaskPage() {
  const { id } = useParams();

  const [mode, setMode] = useState<"browse" | "validate">("browse");
  const [open, setOpen] = useState(false);

  const { mutate: validateMask, error: validateMaskError } = useValidateMask();
  const { data: maskData } = useMaskById(id);

  const {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode,
    isRejectingWholeEpisode,
    canRejectEpisode,
    error: rejectWholeEpisodeError,
  } = useRejectWholeEpisode({
    episodeId: maskData?.episode.id || "",
    shouldNavigateToNext: mode === "validate",
  });

  if (!id) {
    return null;
  }

  const accept = () => {
    validateMask(
      { id, action: ValidationAction.accept },
      {
        onSuccess: () => {
          setMode("browse");
        },
      },
    );
  };

  const reject = (reasons: string[], notes?: string) => {
    validateMask(
      {
        id,
        action: ValidationAction.reject,
        rejectionReasons: reasons,
        notes,
      },
      {
        onSuccess: () => {
          setMode("browse");
        },
        onSettled: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <Box sx={{ marginBottom: mode === "browse" ? 2 : 13 }}>
      <ApiErrorSnackbar error={validateMaskError} />
      <ApiErrorSnackbar error={rejectWholeEpisodeError} />
      <MaskDetails
        backButton
        mode={mode}
        id={id}
        onModeChange={setMode}
        onRejectWholeEpisode={
          canRejectEpisode
            ? () => setConfirmRejectWholeEpisode(true)
            : undefined
        }
      />
      {mode === "validate" && (
        <>
          <RejectionDialog
            asset={{ assetId: id, type: ValidationAssetType.mask }}
            open={open}
            onClose={() => setOpen(false)}
            onReject={reject}
          />
          <ValidationControls
            disabled={open || confirmRejectWholeEpisode}
            onReject={() => setOpen(true)}
            onAccept={accept}
          />
        </>
      )}
      <RejectWholeEpisodeDialog
        open={confirmRejectWholeEpisode}
        onClose={() => setConfirmRejectWholeEpisode(false)}
        onConfirm={handleRejectWholeEpisode}
        isLoading={isRejectingWholeEpisode}
      />
    </Box>
  );
}
